import * as d3 from 'd3'

import Utils from '@/api/Utils';

import { addDays, datetimeToStringFormat } from '@/components/common/dateUtils';

const BACKGROUND_COLOR = 'white';
const GRID_COLOR = 'grey';
const BREAK_COLOR = '#cc2200';
const NO_DATA_COLOR = '#999999';
const BREAK_HEIGHT = 15;
const LINE_COLOR = 'black';
const LINE_WIDTH = 1;


const dateFormatterYear = date => datetimeToStringFormat(date, 'YYYY-MM-DD HH:mm');
const dateFormatterMonth = date => datetimeToStringFormat(date, 'MM-DD HH:mm');
const timeFormatYMD = d3.utcFormat('%Y-%m-%d');
const timeFormatHM = d3.utcFormat('%H:%M');
const xAxisTickFormat = date => {
	if (date.getUTCMinutes() === 0 && date.getUTCHours() === 0) {
		return timeFormatYMD(date);
	}
	return timeFormatHM(date);
}


const addBoldTicks = axis => {
	axis.selectAll('.tick text').attr('class', date => {
		return (date.getUTCMinutes() === 0 && date.getUTCHours() === 0) ? 'tick-bold' : '';
	});
};


const makeGrid = (container, xAxis_grid, height) => {
	container.insert('g', '#scatterplot')
		.attr('class', 'grid grid-x')
		.attr('transform', 'translate(0,' + height + ')')
		.call(xAxis_grid.tickSize(-height).tickFormat(''));

	container.selectAll('.grid')
		.selectAll('line')
		.attr('stroke', GRID_COLOR)
		.style('opacity', 0.5);
}


const getBreaksByMountpoint = (breaksData, mountpoints) => {
	let breaksByMountpoint = {};
	let cleanBreaksData = [];
	for (let mountpoint of mountpoints) {
		breaksByMountpoint[mountpoint] = [];
	}
	for (let item of breaksData) {
		if (item.mountpoint in breaksByMountpoint) {
			const item2 = { ...item, realData: true };
			breaksByMountpoint[item.mountpoint].push(item2);
			cleanBreaksData.push(item2);
		}
	}
	return { breaksByMountpoint, cleanBreaksData };
}


const calcMaxLabelWidth = (svgRoot, mountpoints) => {
	const measurements = svgRoot.append('g').attr('class', 'measurements');
	let maxLabelWidth = 0;
	mountpoints.forEach(item => {
		const textElement = measurements.append('g').attr('class', 'tick').append('text').text(Utils.trimMountpoint(item));
		const bbox = textElement.node().getBBox();
		if (bbox.width > maxLabelWidth) {
			maxLabelWidth = bbox.width;
		}
	});
	measurements.remove();
	return maxLabelWidth + 10;
}


const renderBreaksGantt = (
	chartClass,
	breaksData,
	breaksNoData,
	mountpoints,
	foundMountpoints,
	mountpointsIdMap,
	showAllBreaks,
	svgWidth,
	startDate,
	endDate,
	delaysMaxDatetime,
	router,
	messagesRef,
	zoomStateRef,
) => {
	const { breaksByMountpoint, cleanBreaksData } = getBreaksByMountpoint(breaksData, mountpoints);

	var svgRoot = d3.select(chartClass);
	svgRoot.selectAll('*').remove();

	const svgHeight = Math.max(500, mountpoints.length * 20);

	// left -- место под метки для оси Oy
	const margin = { top: 20, right: 20, bottom: 90 + 20, left: calcMaxLabelWidth(svgRoot, mountpoints) },
		margin2 = { top: svgHeight - margin.bottom + 5, right: 20, bottom: 30, left: 40 },
		width = svgWidth - margin.left - margin.right,
		height = svgHeight - margin.top - margin.bottom,
		height2 = svgHeight - margin2.top - margin2.bottom - margin.top;

	svgRoot.attr('width', width).attr('height', height + margin.top + margin.bottom);

	var xMin;
	if (startDate) {
		xMin = new Date(startDate);
	} else {
		xMin = d3.min(cleanBreaksData, item => item.x1);
	}

	var xMax;
	var dataMax = d3.max(cleanBreaksData, item => item.x2);
	if (delaysMaxDatetime) {
		dataMax = new Date(Math.max(delaysMaxDatetime, dataMax));
	}
	if (endDate) {
		xMax = new Date(addDays(endDate, 1));
	} else {
		xMax = dataMax;
	}
	if (dataMax != null && xMax > dataMax) {
		xMax = dataMax;
	}

	mountpoints.forEach(mountpoint => {
		if (!foundMountpoints.includes(mountpoint)) {
			const silentStationItem = {
				x1: xMin,
				x2: xMax,
				mountpoint: mountpoint,
				realData: false,
			};
			cleanBreaksData.push(silentStationItem);
			breaksByMountpoint[mountpoint] = [silentStationItem];
		}
	});

	var xScale1 = d3.scaleUtc().range([0, width]);
	var xScale2 = d3.scaleUtc().range([0, width]);
	var yScale = d3.scaleBand().rangeRound([0, height]).paddingOuter(0).round(false);

	var svg = svgRoot.append('g').attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

	svg.append('rect')
		.attr('width', '100%')
		.attr('height', height)
		.attr('fill', BACKGROUND_COLOR);

	svg.append('defs').append('svg:clipPath')
		.attr('id', 'clip')
		.append('svg:rect')
		.attr('width', width)
		.attr('height', height)
		.attr('x', 0)
		.attr('y', 0);

	const lineChart = svg.append('g')
		.attr('class', 'focus')
		.attr('clip-path', 'url(#clip)');

	var focus = svg.append('g').attr('class', 'focus2');
	var context = svg.append('g').attr('class', 'context').attr('transform', 'translate(' + 0 + ',' + margin2.top + ')');

	xScale1.domain([xMin, xMax]);
	xScale2.domain(xScale1.domain());
	yScale.domain(mountpoints);

	var xAxis = d3.axisBottom(xScale1),
		xAxis2 = d3.axisBottom(xScale2),
		yAxis = d3.axisLeft(yScale);

	svgRoot.append('text')
		.attr('class', 'x-axis-legend')
		.attr('dominant-baseline', 'middle')
		.attr('text-anchor', 'middle')
		.attr('x', svgWidth / 2)
		.attr('y', svgHeight - 10)
		.text('GPS time');

	xAxis.tickFormat(xAxisTickFormat);
	xAxis2.tickFormat(xAxisTickFormat);

	var xAxis_grid = d3.axisBottom(xScale1);
	makeGrid(focus, xAxis_grid, height);

	mountpoints.forEach(item => {
		lineChart.append('line')
			.style('stroke', LINE_COLOR)
			.style('stroke-width', LINE_WIDTH)
			.style('fill', 'none')
			.attr('x1', xScale1(xMin))
			.attr('y1', yScale(item))
			.attr('x2', xScale1(xMax))
			.attr('y2', yScale(item))
			.attr('transform', 'translate(0, ' + yScale.bandwidth() / 2 + ')');
	});

	// xAxis1
	const xAxisGeometry = focus.append('g')
		.attr('class', 'axis axis--x')
		.attr('transform', 'translate(0,' + height + ')')
		.call(xAxis);
	addBoldTicks(xAxisGeometry);

	const yAxisGeometry = svg.append('g')
		.attr('class', 'y axis')
		.call(yAxis);

	yAxisGeometry.selectAll('.tick')
		.style('cursor', 'pointer')
		.on('click', function (mountpoint) {
			router.push({
				name: 'base-stations-edit',
				params: { id: mountpointsIdMap[mountpoint] },
			});
		})
		.select('text').text(Utils.trimMountpoint)
		.append('title').text(item => item);

	// xAxis2
	const xAxis2Geometry = context.append('g')
		.attr('class', 'axis axis--x')
		.attr('transform', 'translate(0,' + (height2 - 20) + ')')
		.call(xAxis2);
	addBoldTicks(xAxis2Geometry);

	lineChart.selectAll('.line2')
		.data(cleanBreaksData)
		.enter()
		.append('line')
		.style('stroke', item => item.realData ? BREAK_COLOR : NO_DATA_COLOR)
		.attr('class', 'line2')
		.style('stroke-width', BREAK_HEIGHT)
		.attr('transform', 'translate(0, ' + yScale.bandwidth() / 2 + ')');

	lineChart.selectAll('.no-data-rect')
		.data(breaksNoData)
		.enter()
		.append('rect')
		.attr('fill', '#ffffff')
		.attr('opacity', 0.8)
		.attr('stroke', '#444')
		.attr('class', 'no-data-rect');

	const drawData = () => {
		lineChart.selectAll('.line2')
			.attr('x1', (function (d) { return xScale1(d.x1); }))
			.attr('y1', (function (d) { return yScale(d.mountpoint); }))
			.attr('x2', (function (d) {
				if (!showAllBreaks)
					return xScale1(d.x2);
				if ((xScale1(d.x2) - xScale1(d.x1)) <= 1)
					return xScale1(d.x1) + 1;
				else
					return xScale1(d.x2);
			}))
			.attr('y2', (function (d) { return yScale(d.mountpoint); }));
		const xAxisGeometry = focus.select('.axis--x').call(xAxis);
		addBoldTicks(xAxisGeometry);
		focus.select('.grid-x')
			.call(xAxis_grid.tickSize(-height).tickFormat(''))
			.selectAll('line')
			.attr('stroke', GRID_COLOR)
			.style('opacity', 0.5);

		const PADDING = 5;
		lineChart.selectAll('.no-data-rect')
			.attr('x', d => xScale1(d.x1))
			.attr('y', -PADDING)
			.attr('width', d => xScale1(d.x2) - xScale1(d.x1))
			.attr('height', height + PADDING);
	}

	const brushed = () => {
		if (d3.event.sourceEvent && d3.event.sourceEvent.type === 'zoom') return; // ignore brush-by-zoom
		var s = d3.event.selection || xScale2.range();
		xScale1.domain(s.map(xScale2.invert, xScale2));
		if (zoomStateRef) {
			const full = xScale2.range();
			const isFull = s[0] <= full[0] && s[1] >= full[1];
			zoomStateRef.value = isFull ? null : { fullDomain: [xMin, xMax], selection: xScale1.domain() };
		}
		drawData();
	}
	var brush = d3.brushX().extent([[0, 0], [width, height2 - 20]]).on('brush end', brushed);

	const zoomed = () => {
		if (d3.event.sourceEvent && d3.event.sourceEvent.type === 'brush') return; // ignore zoom-by-brush
		var t = d3.event.transform;
		xScale1.domain(t.rescaleX(xScale2).domain());
		drawData();
		context.select('.brush').call(brush.move, xScale1.range().map(t.invertX, t));
	}
	// var zoom =
	d3.zoom().scaleExtent([1, Infinity]).translateExtent([[0, 0], [width, height]]).extent([[0, 0], [width, height]]).on('zoom', zoomed);

	let initialSelection = xScale1.range();
	const saved = zoomStateRef && zoomStateRef.value;
	if (saved
		&& saved.fullDomain[0].getTime() === xMin.getTime()
		&& saved.fullDomain[1].getTime() === xMax.getTime()) {
		initialSelection = saved.selection.map(xScale2);
	}
	context.append('g').attr('class', 'brush').call(brush).call(brush.move, initialSelection);

	let focusWrapper = focus.append('g').attr('class', 'my_focus').style('display', 'none');
	focusWrapper.append('circle')
		.attr('r', 6)
		.attr('fill', 'none')
		.attr('opacity', 0.7)
		.attr('stroke', '#444');
	focusWrapper.append('circle')
		.attr('r', 7)
		.attr('fill', 'none')
		.attr('opacity', 0.7)
		.attr('stroke', 'black');

	var textTipGroup = focusWrapper.append('g')
	var textTipRect = textTipGroup.append('rect')
		.attr('class', 'text_tip_bg')
		.attr('x', -20)
		.attr('y', -5)
		.attr('rx', 5)
		.attr('width', 210)
		.attr('height', 25)
		.attr('fill', 'white')
	var text_tip = textTipGroup.append('text')
		.attr('class', 'text_tip')
		.attr('x', 9)
		.attr('dx', '-2em')
		.attr('dy', '1em')
		.attr('fill', 'black')

	// ZOOM + mousemove
	focus.append('rect')
		.attr('class', 'zoom')
		.attr('width', width)
		.attr('height', height)
		.attr('pointer-events', 'all')
		.on('mousemove', mousemove)
		.on('mouseout', function () {
			focus.select('.my_focus').style('display', 'none');
			textTipGroup.style('display', 'none');
		})
	// .call(zoom); // commented to ignore clicks on chart

	const yScaleToMountpoint = d3.scaleQuantize().domain(yScale.range()).range(yScale.domain());

	var textTipBBox = {
		width: 0,
		height: 0,
	};

	function getBB(selection) {
		selection.each(function () {
			textTipBBox.width = this.getBBox().width;
			textTipBBox.height = this.getBBox().height;
		})
	}

	const checkNoDataPeriod = dt => {
		for (let item of breaksNoData) {
			if (item.x1 <= dt && dt <= item.x2) {
				return item;
			}
		}
		return null;
	}

	const updateTooltip = (title, xPos, yPos, dataStr) => {
		focus.select('.my_focus').style('display', null);
		textTipGroup.style('display', null);
		const str_text = title + ': ' + dataStr;
		focus.select('.my_focus').attr('transform', 'translate(' + xPos + ',' + yPos + ')');
		const delta_x = (xPos > svgWidth / 2) ? - textTipBBox.width - 20 : 40;
		const delta_y = (yPos > 100) ? -25 : 10;
		text_tip.text(str_text).call(getBB);
		textTipGroup.attr('transform', 'translate(' + delta_x + ',' + delta_y + ')');
		textTipRect.attr('width', textTipBBox.width + 8).attr('height', textTipBBox.height + 9);
	}

	function mousemove() {
		var dt = xScale1.invert(d3.mouse(this)[0]);
		const noDataPeriod = checkNoDataPeriod(dt);
		if (noDataPeriod != null) {
			updateTooltip(messagesRef.value.turnedOff, d3.mouse(this)[0], d3.mouse(this)[1], dateFormatterYear(noDataPeriod.x1) + ' - ' + dateFormatterMonth(noDataPeriod.x2));
			return;
		}
		var y_p = d3.mouse(this)[1];
		var GS = yScaleToMountpoint(y_p);
		var dt_list = breaksByMountpoint[GS];
		let i_val = -1;
		let i_val_left = -1;
		let i_val_right = -1;
		var graph_type = -1;
		var delta_left_min = 1000000000000;
		var delta_right_min = delta_left_min;
		for (var i = 0; i < dt_list.length; i++) {
			var x1 = dt_list[i].x1;
			var x2 = dt_list[i].x2;
			if (x1 <= dt && dt <= x2) {
				graph_type = 1;
				i_val = i;
				break;
			}
			if (x2 <= dt) {
				let delta = dt - x2;
				if (delta < delta_left_min) {
					graph_type = 2;
					i_val_left = i;
					delta_left_min = delta;
				}
			}
			if (dt <= x1) {
				let delta = x1 - dt;
				if (delta < delta_right_min) {
					graph_type = 2;
					i_val_right = i;
					delta_right_min = delta;
				}
			}
		}
		var xPos;
		if (graph_type == 2) {
			if (delta_left_min < delta_right_min) {
				xPos = xScale1(dt_list[i_val_left].x2);
				i_val = i_val_left;
			}
			else {
				xPos = xScale1(dt_list[i_val_right].x1);
				i_val = i_val_right;
			}
		}
		else {
			xPos = xScale1(dt);
		}
		if (graph_type != -1) {
			const yPos = (yScale(GS) + yScale.bandwidth() / 2);
			if (dt_list[i_val].realData) {
				updateTooltip(GS, xPos, yPos, dateFormatterYear(dt_list[i_val].x1) + ' - ' + dateFormatterMonth(dt_list[i_val].x2));
			} else {
				updateTooltip(GS, xPos, yPos, messagesRef.value.noData);
			}
		}
	}
	// end mousemove()


}

export {
	renderBreaksGantt,
}
